import {
  HR_DASHBOARD_REQUEST, HR_DASHBOARD_SUCCESS, HR_DASHBOARD_FAILURE,
  HR_SETTINGS_REQUEST, HR_SETTINGS_SUCCESS, HR_SETTINGS_FAILURE,
  HR_SETTINGS_SAVE_REQUEST, HR_SETTINGS_SAVE_SUCCESS, HR_SETTINGS_SAVE_FAILURE,
  HR_IMPORT_REQUEST, HR_IMPORT_SUCCESS, HR_IMPORT_FAILURE,
  HR_EMPLOYEE_SEARCH_REQUEST, HR_EMPLOYEE_SEARCH_SUCCESS, HR_EMPLOYEE_SEARCH_FAILURE, HR_EMPLOYEE_SEARCH_CLEAR,
  HR_COMPANIES_REQUEST, HR_COMPANIES_SUCCESS, HR_COMPANIES_FAILURE,
  HR_CONTEXT_REQUEST, HR_CONTEXT_SUCCESS, HR_CONTEXT_FAILURE,
  HR_SNAPSHOTS_REQUEST, HR_SNAPSHOTS_SUCCESS, HR_SNAPSHOTS_FAILURE,
  HR_SNAPSHOT_SAVE_REQUEST, HR_SNAPSHOT_SAVE_SUCCESS, HR_SNAPSHOT_SAVE_FAILURE,
  HR_SNAPSHOT_LOAD_REQUEST, HR_SNAPSHOT_LOAD_SUCCESS, HR_SNAPSHOT_LOAD_FAILURE
} from 'Store/Actions/hrAnalyticsActions';

const initialState = {
  dashboard: null,
  settings: null,
  loading: false,
  settingsLoading: false,
  savingSettings: false,
  importing: false,
  importResult: null,
  searching: false,
  searchResults: [],
  error: null,
  settingsError: null,
  lastUpdatedAt: null,
  companies: [],
  companiesLoading: false,
  companiesError: null,
  context: null,
  contextLoading: false,
  snapshots: [],
  snapshotsLoading: false,
  snapshotSaving: false,
  snapshotLoading: false,
  snapshotsError: null,
  snapshotError: null,
  lastSavedSnapshot: null,
  dashboardRequestKey: null,
  settingsLoadRequestKey: null,
  settingsSaveRequestKey: null,
  contextRequestKey: null,
  snapshotsRequestKey: null,
  snapshotSaveRequestKey: null,
  snapshotLoadRequestKey: null
};

const isCurrent = (state, action, keyName) =>
  !action.meta?.requestKey || action.meta.requestKey === state[keyName];

export default function hrAnalyticsReducer(state = initialState, action) {
  switch (action.type) {
    case 'HR_DASHBOARD_CLEAR':
      return {
        ...state,
        dashboard: null,
        loading: false,
        error: null,
        lastUpdatedAt: null,
        dashboardRequestKey: null,
        snapshotLoadRequestKey: null
      };

    case 'HR_WORKSPACE_RESET':
      return {
        ...state,
        dashboard: null,
        settings: null,
        loading: false,
        settingsLoading: false,
        savingSettings: false,
        error: null,
        settingsError: null,
        lastUpdatedAt: null,
        context: null,
        contextLoading: false,
        snapshots: [],
        snapshotsLoading: false,
        snapshotLoading: false,
        snapshotSaving: false,
        snapshotsError: null,
        snapshotError: null,
        lastSavedSnapshot: null,
        dashboardRequestKey: null,
        settingsLoadRequestKey: null,
        settingsSaveRequestKey: null,
        contextRequestKey: null,
        snapshotsRequestKey: null,
        snapshotSaveRequestKey: null,
        snapshotLoadRequestKey: null
      };

    case HR_DASHBOARD_REQUEST:
      return { ...state, loading: true, error: null, dashboardRequestKey: action.meta?.requestKey ?? null };
    case HR_DASHBOARD_SUCCESS:
      if (!isCurrent(state, action, 'dashboardRequestKey')) return state;
      return {
        ...state,
        loading: false,
        error: null,
        dashboard: action.payload,
        settings: action.payload?.settings || state.settings,
        context: action.payload?.access || state.context,
        lastUpdatedAt: new Date().toISOString()
      };
    case HR_DASHBOARD_FAILURE:
      if (!isCurrent(state, action, 'dashboardRequestKey')) return state;
      return { ...state, loading: false, error: action.payload };

    case HR_SETTINGS_REQUEST:
      return { ...state, settingsLoading: true, settingsError: null, settingsLoadRequestKey: action.meta?.requestKey ?? null };
    case HR_SETTINGS_SUCCESS:
      if (!isCurrent(state, action, 'settingsLoadRequestKey')) return state;
      return { ...state, settingsLoading: false, settingsError: null, settings: action.payload };
    case HR_SETTINGS_FAILURE:
      if (!isCurrent(state, action, 'settingsLoadRequestKey')) return state;
      return { ...state, settingsLoading: false, settingsError: action.payload };

    case HR_SETTINGS_SAVE_REQUEST:
      return { ...state, savingSettings: true, settingsError: null, settingsSaveRequestKey: action.meta?.requestKey ?? null };
    case HR_SETTINGS_SAVE_SUCCESS:
      if (!isCurrent(state, action, 'settingsSaveRequestKey')) return state;
      return {
        ...state,
        savingSettings: false,
        settingsError: null,
        settings: action.payload,
        dashboard: state.dashboard ? { ...state.dashboard, settings: action.payload } : state.dashboard
      };
    case HR_SETTINGS_SAVE_FAILURE:
      if (!isCurrent(state, action, 'settingsSaveRequestKey')) return state;
      return { ...state, savingSettings: false, settingsError: action.payload };

    case HR_IMPORT_REQUEST:
      return { ...state, importing: true, importResult: null, error: null };
    case HR_IMPORT_SUCCESS:
      return { ...state, importing: false, importResult: action.payload };
    case HR_IMPORT_FAILURE:
      return { ...state, importing: false, error: action.payload };

    case HR_EMPLOYEE_SEARCH_REQUEST:
      return { ...state, searching: true };
    case HR_EMPLOYEE_SEARCH_SUCCESS:
      return { ...state, searching: false, searchResults: action.payload || [] };
    case HR_EMPLOYEE_SEARCH_FAILURE:
      return { ...state, searching: false, searchResults: [] };
    case HR_EMPLOYEE_SEARCH_CLEAR:
      return { ...state, searching: false, searchResults: [] };

    case HR_COMPANIES_REQUEST:
      return { ...state, companiesLoading: true, companiesError: null };
    case HR_COMPANIES_SUCCESS:
      return { ...state, companiesLoading: false, companiesError: null, companies: action.payload || [] };
    case HR_COMPANIES_FAILURE:
      return { ...state, companiesLoading: false, companiesError: action.payload };

    case HR_CONTEXT_REQUEST:
      return { ...state, contextLoading: true, contextRequestKey: action.meta?.requestKey ?? null };
    case HR_CONTEXT_SUCCESS:
      if (!isCurrent(state, action, 'contextRequestKey')) return state;
      return { ...state, contextLoading: false, context: action.payload };
    case HR_CONTEXT_FAILURE:
      if (!isCurrent(state, action, 'contextRequestKey')) return state;
      return { ...state, contextLoading: false, context: null };

    case HR_SNAPSHOTS_REQUEST:
      return { ...state, snapshotsLoading: true, snapshotsError: null, snapshotsRequestKey: action.meta?.requestKey ?? null };
    case HR_SNAPSHOTS_SUCCESS:
      if (!isCurrent(state, action, 'snapshotsRequestKey')) return state;
      return { ...state, snapshotsLoading: false, snapshotsError: null, snapshots: action.payload || [] };
    case HR_SNAPSHOTS_FAILURE:
      if (!isCurrent(state, action, 'snapshotsRequestKey')) return state;
      return { ...state, snapshotsLoading: false, snapshotsError: action.payload, snapshots: [] };

    case HR_SNAPSHOT_SAVE_REQUEST:
      return { ...state, snapshotSaving: true, snapshotError: null, snapshotSaveRequestKey: action.meta?.requestKey ?? null };
    case HR_SNAPSHOT_SAVE_SUCCESS:
      if (!isCurrent(state, action, 'snapshotSaveRequestKey')) return state;
      return { ...state, snapshotSaving: false, snapshotError: null, lastSavedSnapshot: action.payload };
    case HR_SNAPSHOT_SAVE_FAILURE:
      if (!isCurrent(state, action, 'snapshotSaveRequestKey')) return state;
      return { ...state, snapshotSaving: false, snapshotError: action.payload };

    case HR_SNAPSHOT_LOAD_REQUEST:
      return { ...state, snapshotLoading: true, snapshotError: null, snapshotLoadRequestKey: action.meta?.requestKey ?? null };
    case HR_SNAPSHOT_LOAD_SUCCESS:
      if (!isCurrent(state, action, 'snapshotLoadRequestKey')) return state;
      return {
        ...state,
        snapshotLoading: false,
        snapshotError: null,
        dashboard: action.payload,
        settings: action.payload?.settings || state.settings,
        context: action.payload?.access || state.context,
        lastUpdatedAt: new Date().toISOString()
      };
    case HR_SNAPSHOT_LOAD_FAILURE:
      if (!isCurrent(state, action, 'snapshotLoadRequestKey')) return state;
      return { ...state, snapshotLoading: false, snapshotError: action.payload };
    default:
      return state;
  }
}
